import React, {memo} from "react";
import { IFormPartJson } from "../../models";
import { FormPartType } from "../../models/baseTypes";
import SpacerPart from "./SpacerPart";
import PageBreakPart from "./PageBreakPart";
import ErrorMessagePart from './ErrorMessagePart';
import LogoPart from "./LogoPart";
import HeaderPart from "./HeaderPart";
import ContentPart from "./ContentPart";
import SectionPart from "./SectionPart";
import MarkdowPart from "./MarkdownPart";
import RowPart from "./RowPart";
import ColPart from "./ColPart";
import TextField from "../../fields/TextField";
import NumberField from "../../fields/NumberField";
import SelectField from "../../fields/SelectField";
import RadioGroupField from "../../fields/RadioGroupField";
import CheckboxField from "../../fields/CheckboxField";
import CheckboxGroupField from "../../fields/CheckboxGroupField";
import DateField from "../../fields/DateField";
import PhoneNumField from "../../fields/PhoneNumField";
import PostCodeField from "../../fields/PostCodeField";
import StateProvinceField from "../../fields/composite/StateProvinceField";
import PatientConnectionTypeField from "../../fields/composite/PatientConnectionTypeField";
import RelationshipToInsuredField from "../../fields/composite/RelationshipToInsuredField";
import ProviderSelectionField from "../../fields/composite/ProviderSelectionField";
import MedicalQuestionnaireField from "../../fields/composite/MedicalQuestionnaireField";
import SignatureField from "../../fields/SignatureField";
import { handleFieldChanged, handleFormLoaded } from "../fieldChangeHandlers";
import useFieldChangeTracker from "../../useFieldChangeTracker";


const createFormPart = (json: IFormPartJson, key?: string | number) => {

  switch (json.type)
  {
    case FormPartType.Spacer: return <SpacerPart key={key} {...json} />;

    case FormPartType.PageBreak: return <PageBreakPart key={key} {...json} />;

    case FormPartType.ErrorMsg: return <ErrorMessagePart key={key} {...json} />;

    case FormPartType.Logo: return <LogoPart key={key} {...json} />;

    case FormPartType.Header: return <HeaderPart key={key} {...json} />;

    case FormPartType.Content: return <ContentPart key={key} {...json} />;

    case FormPartType.Markdown: return <MarkdowPart key={key} {...json} />;

    case FormPartType.Section:
      const children = json.parts.map((col, i) => createFormPart(col, i));
      return (
        <SectionPart key={key} {...json}>
          {children}
        </SectionPart>);

    case FormPartType.Col: return (
        <ColPart
          key={key}
          mobileWidth={json.mobileWidth}
          tabletWidth={json.tabletWidth}
          desktopWidth={json.desktopWidth}
        >
          {createFormPart(json.part)}
        </ColPart>
      );

    case FormPartType.Row:
      const cols = json.columns.map((col, i) => createFormPart(col, i));
      return (
        <RowPart
            key={key}
            mobileCols={json.mobileCols}
            tabletCols={json.tabletCols}
            desktopCols={json.desktopCols}
          >
            {cols}
        </RowPart>);

    case FormPartType.TextField:
      return <TextField key={key} {...json} />;

    case FormPartType.NumberField:
      return <NumberField key={key} {...json} />;

    case FormPartType.SelectField:
      return <SelectField key={key} {...json} />;

    case FormPartType.RadioGroupField:
        return <RadioGroupField key={key} {...json} />;

    case FormPartType.CheckboxField:
        return <CheckboxField key={key} {...json} />

    case FormPartType.CheckboxGroupField:
        return <CheckboxGroupField key={key} {...json} />

    case FormPartType.DateField:
      return <DateField key={key} {...json} />;

    case FormPartType.PhoneNumField:
        return <PhoneNumField key={key} {...json} />

    case FormPartType.PostCodeField:
        return <PostCodeField key={key} {...json} />

    case FormPartType.SignatureField:
      return <SignatureField key={key} {...json} />
      //return <LazyLoad key={key} componentLoader={signatureModuleLoader} componentProps={json} />

    case FormPartType.StateProvinceField:
      return <StateProvinceField key={key} {...json} />

    case FormPartType.PatientConnectionTypeField:
      return <PatientConnectionTypeField key={key} {...json} />

    case FormPartType.RelationshipToInsuredField:
      return <RelationshipToInsuredField key={key} {...json} />

    case FormPartType.ProviderSelectionField:
      return <ProviderSelectionField key={key} {...json} />;

    case FormPartType.MedicalQuestionnaireField:
      return <MedicalQuestionnaireField key={key} {...json} />;

    default:
      console.warn(`Unexpected form part type: "${(json as IFormPartJson).type}"`);
      return <SpacerPart key={key} {...json} />
  }
}


interface IFormPartProps {
  parts: IFormPartJson[];
  className?: string;
  style?: React.CSSProperties;
}

const FormPartFactory: React.FC<IFormPartProps> = memo(({ parts = [], className, style }) => {
  useFieldChangeTracker(handleFieldChanged, handleFormLoaded);
  return (
    <div className={className} style={style}>
      {parts.map((part, i) => createFormPart(part, i))}
    </div>
  )
});

export default FormPartFactory;

